import React, { useState, useEffect } from 'react';
import axiosInstance from '../../config/axiosInstance';
import { toast } from 'react-toastify';
import AdminChatList from './AdminChatList';
import AdminChatWindow from './AdminChatWindow';

const AdminChatDashboard = ({ onMessageRead }) => {
  const [chatList, setChatList] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch chat list
  const fetchChats = async (showLoading = false) => {
    if (showLoading) setLoading(true);
    try {
      const response = await axiosInstance.get('/api/chat/rooms/all');

      if (response.data.success) {
        const rooms = response.data.data.chatRooms || [];
        const uniqueRooms = Array.from(
          new Map(rooms.map((room) => [room.id, room])).values(),
        ).sort(
          (a, b) =>
            new Date(b.updatedAt || b.createdAt) -
            new Date(a.updatedAt || a.createdAt),
        );
        setChatList(uniqueRooms);
        setSelectedChat((current) =>
          current
            ? uniqueRooms.find((room) => room.id === current.id) || current
            : current,
        );
      }
    } catch (error) {
      console.error('Error fetching chats:', error);
      if (showLoading) toast.error('Failed to load chats');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchChats(true);
    // Poll for new messages every 10 seconds (without showing loading)
    const interval = setInterval(() => {
      fetchChats(false);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
  };

  const handleChatUpdate = () => {
    fetchChats(false);
    if (onMessageRead) {
      onMessageRead();
    }
  };

  return (
    <div className='admin-chat-workspace'>
      {/* CHAT INTERFACE */}
      <div className='admin-chat-grid'>
        <div className='admin-chat-list-panel'>
          <div
            className='card border-0 shadow-sm'
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div className='card-header bg-white border-bottom py-3'>
              <h6 className='mb-0 fw-semibold'>Chat List</h6>
            </div>

            <div
              className='card-body p-0'
              style={{ flex: 1, overflowY: 'auto' }}
            >
              <AdminChatList
                chats={chatList}
                selectedChat={selectedChat}
                onSelectChat={handleSelectChat}
                loading={loading}
              />
            </div>
          </div>
        </div>

        {/* RIGHT PANEL - Chat Window */}
        <div className='admin-chat-conversation-panel'>
          <AdminChatWindow
            selectedChat={selectedChat}
            onChatUpdate={handleChatUpdate}
          />
        </div>
      </div>
      <style>{`
        .admin-chat-workspace { height: calc(100vh - 230px); min-height: 520px; overflow: hidden; }
        .admin-chat-grid { display: grid; grid-template-columns: minmax(280px, 340px) minmax(0, 1fr); gap: 1rem; height: 100%; }
        .admin-chat-list-panel, .admin-chat-conversation-panel { min-width: 0; min-height: 0; }
        @media (max-width: 991.98px) {
          .admin-chat-workspace { height: auto; min-height: 0; overflow: visible; }
          .admin-chat-grid { grid-template-columns: 1fr; height: auto; }
          .admin-chat-list-panel { height: min(360px, 42vh); }
          .admin-chat-conversation-panel { height: max(560px, 68vh); }
        }
        @media (max-width: 575.98px) {
          .admin-chat-grid { gap: .75rem; }
          .admin-chat-list-panel { height: 300px; }
          .admin-chat-conversation-panel { height: 72vh; min-height: 520px; }
        }
      `}</style>
    </div>
  );
};

export default AdminChatDashboard;
